import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Waypoints, Settings, FileText, CheckCircle, ShieldCheck, QrCode, Send, Radar,
  PhoneOutgoing, BarChart3, Bot, User, AlertTriangle, ArrowRight, Sparkles, Mic, FlaskConical,
} from 'lucide-react';

const ROLE_STYLES = {
  drafter: { label: 'Brief-Verantwortlicher', cls: 'text-purple-400 border-purple-500/30 bg-purple-500/10' },
  approver: { label: 'Approver', cls: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
  admin: { label: 'Admin', cls: 'text-sky-400 border-sky-500/30 bg-sky-500/10' },
  system: { label: 'System (automatisch)', cls: 'text-orange-400 border-orange-500/30 bg-orange-500/10' },
  team: { label: 'Team', cls: 'text-slate-300 border-border bg-secondary/40' },
};

const Role = ({ r }) => (
  <span className={`text-[9px] px-1.5 py-0.5 rounded border font-mono uppercase inline-flex items-center gap-1 ${ROLE_STYLES[r].cls}`}>
    {r === 'system' ? <Bot className="w-2.5 h-2.5" /> : <User className="w-2.5 h-2.5" />}
    {ROLE_STYLES[r].label}
  </span>
);

const StatusChip = ({ label, cls }) => (
  <span className={`text-[10px] px-2 py-0.5 rounded border font-mono ${cls}`}>{label}</span>
);

const Auto = ({ children }) => (
  <div className="flex items-start gap-2 bg-orange-500/5 border border-orange-500/20 rounded-sm px-3 py-2 mt-2">
    <Bot className="w-3.5 h-3.5 text-orange-400 mt-0.5 shrink-0" />
    <p className="text-[12px] text-foreground/80 leading-relaxed">{children}</p>
  </div>
);

const B = ({ children }) => <strong className="text-foreground font-semibold">{children}</strong>;

function Step({ num, icon: Icon, title, status, roles = [], children, last = false }) {
  return (
    <div className="flex gap-4" data-testid={`guide-step-${num}`}>
      <div className="flex flex-col items-center shrink-0">
        <div className="w-9 h-9 rounded-full bg-blue-600/15 border border-blue-500/40 flex items-center justify-center">
          <Icon className="w-4 h-4 text-blue-400" strokeWidth={1.5} />
        </div>
        {!last && <div className="w-px flex-1 bg-border my-1.5" />}
      </div>
      <div className={`flex-1 min-w-0 ${last ? '' : 'pb-6'}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] text-muted-foreground/60">{String(num).padStart(2, '0')}</span>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {status}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
          {roles.map(r => <Role key={r} r={r} />)}
        </div>
        <div className="text-[13px] text-foreground/80 leading-relaxed mt-2 space-y-1.5">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function OutreachGuidePage() {
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl space-y-6 animate-fade-in-up" data-testid="outreach-guide-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight flex items-center gap-2.5">
          <Waypoints className="w-7 h-7 text-blue-400" /> Outreach Guide
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Der komplette Prozess der proprietären Eigentümer-Ansprache — vom Entwurf bis zur Antwort. Für neue Teammitglieder Schritt für Schritt erklärt.
        </p>
      </div>

      {/* Status legend */}
      <div className="bg-card border border-border rounded-sm p-4" data-testid="guide-status-legend">
        <p className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-2">Der Brief-Lebenszyklus</p>
        <div className="flex items-center gap-1.5 flex-wrap">
          <StatusChip label="Draft" cls="text-amber-400 bg-amber-400/10 border-amber-500/30" />
          <ArrowRight className="w-3 h-3 text-muted-foreground/40" />
          <StatusChip label="Finalized" cls="text-purple-400 bg-purple-400/10 border-purple-500/30" />
          <ArrowRight className="w-3 h-3 text-muted-foreground/40" />
          <StatusChip label="Approved" cls="text-emerald-400 bg-emerald-400/10 border-emerald-500/30" />
          <ArrowRight className="w-3 h-3 text-muted-foreground/40" />
          <StatusChip label="Sent" cls="text-blue-400 bg-blue-400/10 border-blue-500/30" />
          <ArrowRight className="w-3 h-3 text-muted-foreground/40" />
          <StatusChip label="Responded" cls="text-teal-400 bg-teal-400/10 border-teal-500/30" />
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">
          Jeder Brief durchläuft diese Stufen. Auf der Outreach-Seite ist die Liste danach gruppiert; die Briefdetail-Ansicht zeigt einen Stepper mit Zeitstempeln (wer hat wann finalisiert, freigegeben, versendet).
        </p>
      </div>

      {/* Steps */}
      <div className="bg-card border border-border rounded-sm p-5">
        <Step num={1} icon={Settings} title="Einmalige Vorbereitung" roles={['admin']}>
          <p><B>Team Management → Workflow-Rollen</B>: festlegen, wer finaler Approver ist (gibt jeden Deal frei und prüft fremde Briefe — Standard Max), wer je Prio den Brief schreibt (A/C Max, B Luis) und wer den Owner-Kontakt anlegt (Privatadresse + E-Mail + Telefon; jeder kann die Aufgabe übernehmen). Schreibt der finale Approver selbst, entfällt der Review — Finalisieren = Freigabe.</p>
          <p><B>Outreach → Landing settings</B>: Absenderdaten, Foto, geklonte Stimme (ElevenLabs) und Termin-Buchungslink konfigurieren. Diese Daten personalisieren später jede Landing Page.</p>
        </Step>

        <Step num={2} icon={FileText} title="Deal in Outreach ziehen & Brief entwerfen"
          status={<StatusChip label="Draft" cls="text-amber-400 bg-amber-400/10 border-amber-500/30" />}
          roles={['drafter']}>
          <p>Deal auf der Deal-Seite in die Stage <B>Outreach</B> ziehen und den <B>Kontaktkanal</B> wählen (Brief / E-Mail / Sonstiges). Der Brief-Verantwortliche erhält automatisch den Task „Start outreach".</p>
          <p>Auf der Deal-Seite <B>„Draft with AI"</B> klicken — Claude generiert einen personalisierten Brief aus den Deal-Daten. Der Entwurf erscheint auf der Outreach-Seite unter <B>Drafts</B> und kann dort frei bearbeitet werden (<B>Edit Letter</B>).</p>
        </Step>

        <Step num={3} icon={CheckCircle} title="Finalisieren — zur Freigabe einreichen"
          status={<StatusChip label="Finalized" cls="text-purple-400 bg-purple-400/10 border-purple-500/30" />}
          roles={['drafter']}>
          <p>Wenn der Text steht: <B>„Finalize"</B> in der Briefdetail-Ansicht. Der Brief wandert in <B>Pending approval</B> und steht automatisch auf der Team-Meeting-Agenda.</p>
          <Auto>Der Approver bekommt automatisch einen Freigabe-Task (inkl. Push &amp; MS To Do).</Auto>
        </Step>

        <Step num={4} icon={ShieldCheck} title="Freigabe (Vier-Augen-Prinzip)"
          status={<StatusChip label="Approved" cls="text-emerald-400 bg-emerald-400/10 border-emerald-500/30" />}
          roles={['approver']}>
          <p>Der Approver prüft den Brief und gibt ihn frei. Er erscheint unter <B>Ready to send</B>.</p>
          <Auto>Ab jetzt läuft eine <B>7-Tage-Uhr</B>: Wird der Brief nicht versendet, bekommt er einen roten „Not sent for 7+ days"-Chip, der Outreach-Menüpunkt einen roten Badge und die Zahl fließt in den App-Icon-Badge — nichts bleibt liegen.</Auto>
        </Step>

        <Step num={5} icon={QrCode} title="Landing Page, Medien & QR-Flyer"
          roles={['drafter', 'admin']}>
          <p>In der Briefdetail-Ansicht die <B>persönliche Landing Page</B> erstellen. Sie wird automatisch personalisiert (Anrede + individueller Satz zur Firma — per <B>„3 AI sentence variants"</B> schlägt Claude Alternativen vor).</p>
          <p className="flex items-center gap-1.5 flex-wrap">
            <Mic className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            <span><B>Sprachnachricht</B> (geklonte Stimme) und/oder <B>Video</B> hochladen — die Seite spielt sie dem Eigentümer beim Scannen vor.</span>
          </p>
          <p className="flex items-center gap-1.5 flex-wrap">
            <FlaskConical className="w-3.5 h-3.5 text-violet-400 shrink-0" />
            <span>Optional: dem Brief eine <B>A/B-Test-Variante</B> zuweisen (Video / Nur Stimme / Ohne Medien) — so lernt ihr, welches Format am besten konvertiert.</span>
          </p>
          <p>Zum Schluss den <B>PDF-Flyer mit eingebettetem QR-Code</B> generieren und zusammen mit dem Brief drucken.</p>
        </Step>

        <Step num={6} icon={Send} title="Versenden & als versendet markieren"
          status={<StatusChip label="Sent" cls="text-blue-400 bg-blue-400/10 border-blue-500/30" />}
          roles={['drafter', 'admin']}>
          <p>Brief + Flyer per Post verschicken, dann in der App <B>„Mark Sent"</B> klicken.</p>
          <p>Der Bestätigungsdialog startet das <B>45-Tage-Antwortfenster</B> — und <B>warnt</B>, falls noch keine Landing Page existiert (sonst führt der gedruckte QR-Code ins Leere).</p>
        </Step>

        <Step num={7} icon={Radar} title="Automatisches Tracking — das System übernimmt"
          roles={['system']}>
          <p>Nach dem Versand musst du nichts mehr manuell verfolgen:</p>
          <Auto><B>QR-Scan</B> → Scan-Zähler steigt, das Team bekommt eine Push-Nachricht.</Auto>
          <Auto><B>Rückruf- oder Terminanfrage</B> über die Landing Page → der Brief springt automatisch auf <B>Responded</B>, ein Bearbeitungs-Task mit hoher Priorität wird erstellt, das Team wird benachrichtigt.</Auto>
          <Auto><B>21 Tage ohne Scan und ohne Antwort</B> → automatischer Task <B>„📞 Nachfassen"</B> für den Brief-Verantwortlichen (Push + MS To Do).</Auto>
          <p>Antwortet der Eigentümer <B>telefonisch oder per E-Mail</B>, trägst du das per Button <B>„Response received"</B> am Brief nach.</p>
        </Step>

        <Step num={8} icon={PhoneOutgoing} title="Telefonisch nachfassen & Ergebnis erfassen"
          roles={['drafter']}>
          <p>Beim Erledigen des Nachfass-Tasks fragt ein Dialog: <B>„Wie war das Telefonat?"</B></p>
          <p>• <B className="text-emerald-400">Interessiert</B> — Brief wird „Responded", zählt als Telefon-Response in der A/B-Statistik.<br />
             • <B className="text-amber-400">Später erneut</B> — nach weiteren 21 Tagen entsteht automatisch ein neuer Nachfass-Task.<br />
             • <B className="text-red-400">Kein Interesse</B> — zählt sofort als „No response" (kein 45-Tage-Warten).</p>
          <p>Das Ergebnis erscheint als Chip am Brief und im Activity-Log — nichts geht verloren.</p>
        </Step>

        <Step num={9} icon={BarChart3} title="Auswerten & berichten" roles={['team']} last>
          <p><B>Outreach Statistics</B>: Funnel <B>Sent → Scanned → Responded → Phone follow-up → Interested</B>, das Panel <B>„Where responses come from"</B> (QR vs. Telefon vs. manuell) und die A/B-Tests mit Signifikanz-Prüfung (ab 25 Briefen pro Variante).</p>
          <Auto>Die <B>Investor-Update-E-Mail</B> (Reporting) enthält die Outreach-Zahlen automatisch — nur aggregiert, ohne Firmennamen.</Auto>
        </Step>
      </div>

      {/* Guardrails */}
      <div className="bg-card border border-border rounded-sm p-5" data-testid="guide-guardrails">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-400" /> Eingebaute Schutzmechanismen (Human-Error-Prävention)
        </h2>
        <ul className="space-y-2 text-[13px] text-foreground/80 leading-relaxed list-disc pl-5">
          <li><B>Vier-Augen-Prinzip</B>: Kein Brief geht ohne Freigabe durch eine zweite Person raus.</li>
          <li><B>Landing-Page-Check</B>: „Mark Sent" warnt, wenn keine Landing Page existiert — kein toter QR-Code im Briefkasten.</li>
          <li><B>7-Tage-Warnung</B>: Freigegebene, aber nicht versendete Briefe werden rot markiert (Chip + Sidebar-Badge + App-Icon-Badge).</li>
          <li><B>Auto-Response-Tracking</B>: Landing-Page-Anfragen setzen den Brief selbstständig auf „Responded" — kein manuelles Nachtragen nötig.</li>
          <li><B>21-Tage-Follow-up</B>: Kein Scan, keine Antwort? Das System erstellt den Nachfass-Task von selbst.</li>
          <li><B>45-Tage-Fenster</B>: Danach gilt der Brief als „No response" — die A/B-Statistik bleibt sauber.</li>
          <li><B>Stunden-Job Self-Healing</B>: Versendete Briefe mit vorhandener Landing-Page-Anfrage werden automatisch nachkorrigiert.</li>
        </ul>
      </div>

      {/* Quick links */}
      <div className="flex items-center gap-2 flex-wrap pb-4">
        {[
          { label: 'Zur Outreach-Seite', to: '/outreach', icon: Send },
          { label: 'Outreach Statistics', to: '/outreach-stats', icon: BarChart3 },
          { label: 'Handbuch', to: '/manual', icon: Sparkles },
        ].map(l => (
          <button key={l.to} onClick={() => navigate(l.to)} data-testid={`guide-link-${l.to.replace(/\//g, '')}`}
            className="inline-flex items-center gap-1.5 text-[11px] text-blue-400 hover:text-blue-300 border border-blue-900/40 rounded-sm px-3 py-1.5 transition-colors">
            <l.icon className="w-3 h-3" /> {l.label}
          </button>
        ))}
      </div>
    </div>
  );
}
